import { format, differenceInDays, differenceInYears, intervalToDuration, isBefore, isAfter, addDays, addWeeks, addMonths, addYears, isLeapYear, isEqual } from 'date-fns';
import { PaymentFrequency } from '@general/js/enums';

/**
 * Returns current date in `yyyy-mm-dd`, ignoring time.
 *
 * @returns Date string in `yyyy-mm-dd` format
 */
export const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();

  let month = today.getMonth() + 1;
  let date = today.getDate();

  if (month < 10) {
    month = `0${month}`;
  }

  if (date < 10) {
    date = `0${date}`;
  }

  return `${year}-${month}-${date}`;
};

/**
 * Get the past or later date of an initial date after given amount of years
 *
 * @param {date} initialDate - The initial date
 * @param {number} years - The number of years to transform initial date by.
 *                          If the number is negative, it is a past date, is positive then it is a future date
 * @returns {string} - The transformed date formatted as "YYYY-MM-DD"
 */
export const getPastOrLaterDateFromInitialDate = (initialDate, years) => {
  const laterDate = new Date(initialDate);
  laterDate.setFullYear(laterDate.getFullYear() + years);
  const year = laterDate.getFullYear();
  let month = laterDate.getMonth() + 1;
  let date = laterDate.getDate();

  if (month < 10) {
    month = `0${month}`;
  }

  if (date < 10) {
    date = `0${date}`;
  }

  return `${year}-${month}-${date}`;
};

/**
 * Format the normal date form "Wed Apr 07 2021 14:05:50 GMT+1000 (Australian Eastern Standard Time)" or "2001-05-05T05:09:41.6913125Z" to "YYYY-MM-DD"
 * The date passing in has to be the above date format.
 *
 * @param {string} date - The date needed to be formated.
 * @param {boolean} zeroTimeOffset - Boolean to determine whether to apply zero UTC offset (time zone offset) - default is false
 * @returns {string} - The formatted date as "YYYY-MM-DD"
 */
export const formatDate = (date, zeroTimeOffset = false, useLocalTime = false) => {
  if (!date) {
    return '';
  }
  // Check whether to apply zero time offset or if is already applied (Z at end of string)
  const applyZeroTimeOffset = zeroTimeOffset && date.slice(-1) !== 'Z';

  // Considersing the time zone offset, using UTC
  const dateToFormat = new Date(applyZeroTimeOffset ? `${date}Z` : date);
  const newYear = useLocalTime ? dateToFormat.getFullYear() : dateToFormat.getUTCFullYear();
  const newMonth = String((useLocalTime ? dateToFormat.getMonth() : dateToFormat.getUTCMonth()) + 1).padStart(2, '0');
  const newDay = String(useLocalTime ? dateToFormat.getDate() : dateToFormat.getUTCDate()).padStart(2, '0');

  return [newYear, newMonth, newDay].join('-');
};

/**
 * Transform the date format from "DD / MM / YYYY" to "YYYY-MM-DD"
 *
 * @param {string} date - The date entered to be transformed.
 * @param {bool} returnNull - By default is false. if true, return `null` instead of `''` when @param date is incorrect format
 * @returns {string} - The date in "YYYY-MM-DD" format.
 */
export const formatFormDate = (date, returnNull = false) => {
  if (date && date.split !== undefined) {
    return date.split(' / ').reverse().join('-');
  }
  return returnNull ? null : '';
};

/**
 * Transform the date format from "DD / MM / YYYY" to "YYYY-MM-DDTHH:mm:ss.sssZ" the ISO format
 *
 * @param {string} date - The date entered to be transformed.
 * @returns {string} - The date in "YYYY-MM-DDTHH:mm:ss.sssZ" format.
 */
export const convertFormDateToISOString = (date) => {
  // this only works when date is not empty string nor null nor undefined and `DD /MM / YYYY` format
  if (date !== '' && date !== null && date !== undefined && RegExp(/(\d{2}\s\/\s\d{2}\s\/\s\d{4})/).test(date)) {
    return new Date(formatFormDate(date)).toISOString();
  }
  return null;
};

/**
 * Transform the date format from "YYYY-MM-DD" to "DD Month YYYY" (e.g. 12 January 2020)
 *
 * @param {string} dateString
 * @returns {string} - The date in "DD Month YYYY" format or undefined if date is invalid
 */
export const convertDateStringToText = (dateString) => {
  const date = new Date(dateString);
  // ensure date is correct format
  if (date instanceof Date && !Number.isNaN(date.valueOf())) {
    const month = new Date(date).toLocaleString('en-us', { month: 'long' });
    return `${date.getDate()} ${month} ${date.getFullYear()}`;
  }
  return undefined;
};

/**
 * Transform the date format from "YYYY-MM-DD" to "DD / MM / YYYY"
 *
 * @param {string} date - The date entered to be transformed.
 * @returns {string} - The date in "DD / MM / YYYY" format.
 */
export const reverseFormatFormDate = (date) => {
  if (date) {
    return date.split('-').reverse().join(' / ');
  }
  return '';
};

/**
 * Transform the date format from ISO Dates like "1989-12-12T00:00:00" to "DD / MM / YYYY"
 *
 * @param {string} date - The date entered to be transformed.
 * @returns {string} - The date in "DD / MM / YYYY" format.
 */
export const convertISOToDisplay = (date) => {
  return format(new Date(date), 'dd / MM / yyyy');
};

/**
 * Checks whether the given date string equals to today's date or a future date.
 *
 * @param {string} date - The date in 'dd / MM / yyyy' format.
 * @returns {boolean} - False if the given date string is equal to today's date or a future date, and true otherwise.
 */
export const isPastDate = (date) => {
  const today = new Date();
  const thisDate = new Date(formatFormDate(date));

  // eliminate time difference by setting both dates' time to 00:00:00
  const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
  const givenDate = new Date(thisDate.getFullYear(), thisDate.getMonth(), thisDate.getDate(), 0, 0, 0);

  if (date.length === 14) {
    return currentDate > givenDate;
  }

  if (date.length === 9) {
    // This is to support when the date passed is in is MM / yyyy format.
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    // Convert the string into numeric values
    const [monthString, yearString] = date.split(' / ');
    const year = Number.parseInt(yearString, 10);
    const month = Number.parseInt(monthString, 10);

    if (currentYear > year) {
      return true;
    }

    if (currentYear === year) {
      if (currentMonth > month) {
        return true;
      }
      return false;
    }

    return false;
  }
  return true;
};

/**
 * Checks whether the given date string equals to future date.
 *
 * @param {string} date - The date in 'dd / MM / yyyy' format.
 * @returns {boolean} - False if the given date string is equal to a future date, and true otherwise.
 */
export const isPastDateExcludingToday = (date) => {
  const currentDate = new Date();
  if (date.length === 14) {
    const givenDate = new Date(formatFormDate(date));
    // Since we only compare date (without time-stamp), setting time-stamp part of the Date object to 00:00:00:00
    return currentDate.setHours(0, 0, 0, 0) > givenDate.setHours(0, 0, 0, 0);
  }
  return true;
};

/**
 * Checks whether the given date string equals to future date excluding today.
 *
 * @param {string} date - The date in 'dd / MM / yyyy' format.
 * @returns {boolean} - False if the given date string is equal to a future date excluding today, and true otherwise.
 */
export const isPastDateIncludingToday = (date) => {
  const currentDate = new Date();
  if (date.length === 14) {
    const givenDate = new Date(formatFormDate(date));
    // Since we only compare date (without time-stamp), setting time-stamp part of the Date object to 00:00:00:00
    return currentDate.setHours(0, 0, 0, 0) >= givenDate.setHours(0, 0, 0, 0);
  }
  return true;
};

/**
 * Checks whether the given date string is a past date before yesterday.
 *
 * @param {string} date - The date in 'dd / MM / yyyy' format.
 * @returns {boolean} - False if the given date string is equal to a past date excluding today minus 1 day, and true otherwise.
 */
export const isPastDateBeforeYesterday = (date) => {
  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - 1); // Subtract 1 day from the current date

  if (date.length === 14) {
    const givenDate = new Date(formatFormDate(date));
    // Compare dates without time by setting time to 00:00:00
    return currentDate.setHours(0, 0, 0, 0) > givenDate.setHours(0, 0, 0, 0);
  }
  return true;
};

/**
 * Calculate the age of the person from its birthday. Date has to be a valid date.
 *
 * @param {string} date - The date of DOB entered from the form in 'dd / mm / yyyy' format.
 * @param {string} dateToCompare - Optional. if given, calculate age as of given date instead of now. format should be 'dd / mm / yyyy'
 * @returns {int} - The age calculated.
 */
export const calculateAge = (date, dateToCompare = undefined) => {
  const dob = new Date(formatFormDate(date));
  const now = dateToCompare === undefined ? new Date(Date.now()) : new Date(formatFormDate(dateToCompare));
  let age = now.getFullYear() - dob.getFullYear();

  // if birthday has not passed, take one year out
  const nowMonthNotPassedDOBMonth = now.getMonth() < dob.getMonth(); // if current month has not passed dob month, birthday has not passed yet
  const nowDateNotPassedDOBDateWhenMonthsEqual = now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate(); // if current month is equal to dob month and current date has not passed DOB date, birthday has not passed
  if (nowMonthNotPassedDOBMonth || nowDateNotPassedDOBDateWhenMonthsEqual) {
    age -= 1;
  }

  return age;
};

/**
 * Calculate CDA. Date has to be a valid date.
 *
 * @param {date} birthDate in "dd / mm / yyyy" format. this birthday age must be within 18 to 29.
 * @param {date} coverStartDate  in "dd / mm / yyyy" format
 */
export const calculateCertifiedDiscountAge = (birthDate, coverStartDate) => {
  const startDate = new Date(formatFormDate(coverStartDate));
  const dob = new Date(formatFormDate(birthDate));
  const ageToBeAsOfCoverStartDate = startDate.getFullYear() - dob.getFullYear();
  let cda = ageToBeAsOfCoverStartDate;

  // check if birthday as of cover start date has passed at the date of cover start date, take one year out from CDA. cda can't be below 18
  const startDateMonthNotPassedDOBMonth = startDate.getMonth() < dob.getMonth(); // if start date month is has not passed dob month, birthday has not passed yet
  const startDateNotPassedDOBDateWhenMonthsEqual = startDate.getMonth() === dob.getMonth() && startDate.getDate() < dob.getDate(); // if start date month is equal to dob month and start cover date has not passed DOB date, birthday has not passed
  if (startDateMonthNotPassedDOBMonth || startDateNotPassedDOBDateWhenMonthsEqual) {
    cda -= 1;
  }

  return cda;
};

/**
 * Check if the age is over 125.
 *
 * @param {string} date - The date of DOB entered.
 * @returns {boolean} - True when the age is under or equal to 125, false when the age is over 125.
 */
export const overAge = (date) => {
  if (date.length === 14) {
    return differenceInYears(new Date(), new Date(formatFormDate(date))) <= 125;
  }
  return true;
};

/**
 * Check if the age is under 18.
 *
 * @param {string} date - The date of DOB entered.
 * @returns {boolean} - True when the age is over or equal to 18, false when the age is under 18.
 */
export const underAge = (date) => {
  if (date.length === 14 && isPastDate(date)) {
    const age = calculateAge(date);
    return age >= 18;
  }

  return true;
};

/**
 * Function that checks if the date is up to 60 days from today.
 *
 * @param {string} date - The date to check in format of 'DD / MM / YYYY'.
 * @returns {boolean} - True if it's within 60 days or not a valid date yet, false if not.
 */
export const checkFutureDateIn60Days = (date) => {
  if (date !== '' && date !== null) {
    const currentDate = new Date();
    const givenDate = new Date(formatFormDate(date));
    const diff = givenDate.getTime() - currentDate.getTime();
    if (diff / (1000 * 60 * 60 * 24) <= 60 && diff / (1000 * 60 * 60 * 24) > 0 && date.length === 14) {
      return true;
    }
    return false;
  }

  return true;
};

/**
 * Check if the date is more than 109 years ago.
 *
 * @param {string} date - The date entered.
 * @returns {boolean} - True when the date is equal to 109 years or less, false when date is more than 109 years ago.
 */
export const overPastDate = (date) => {
  if (date.length === 14 && isPastDate(date)) {
    const currentDate = new Date();
    const formattedDate = new Date(formatFormDate(date));

    const duration = intervalToDuration({ end: currentDate, start: formattedDate });
    if (!duration.years) {
      duration.years = 0;
    }
    if (duration.years < 109) {
      return true;
    }

    return duration.years === 109 && duration.months === 0 && duration.days === 0 && duration.hours === 0 && duration.minutes === 0;
  }

  return true;
};

/**
 * Check if the date is before year 1970.
 *
 * @param {string} date - The date entered.
 * @returns {boolean} - True when the date is equal to 109 years or less, false when date is more than 109 years ago.
 */
export const beforeYear1970 = (date) => {
  if (date.length === 14 && isPastDate(date)) {
    const dateToCheck = new Date(formatFormDate(date));
    return dateToCheck.getFullYear() >= 1970;
  }

  return true;
};

/**
 * Check if the date is within 90 days from current day.
 *
 * @param {string} date - The date entered.
 * @returns {boolean} - True when the date is equal to 90 days or less, false when date is more than 90 days in the future.
 */
export const lessThan90Days = (date) => {
  if (date.length === 14 && !isPastDate(date)) {
    const today = new Date(Date.now());
    const targetDay = new Date(formatFormDate(date));

    // set both dates with time 00:00:00 to make sure getting absolute accurate diffrence in days
    // NOTE: according to date-fns documentation, `differenceInDays` method consider time as well hence setting time to 00:00:00 is required in our use case
    const dateLeft = new Date(targetDay.getFullYear(), targetDay.getMonth(), targetDay.getDate(), 0, 0, 0);
    const dateRight = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);

    const diff = differenceInDays(dateLeft, dateRight);

    return diff <= 90;
  }

  return true;
};

/**
 * Check if the date is before given date
 *
 * @param {string} dateBefore - The date supposed to be before, in DD / MM / YYYY format.
 * @param {string} dateAfter - The date supposed to be after,  in DD / MM / YYYY format.
 * @returns {boolean} - True when the date is really before, false otherwise.
 */
export const isDateBefore = (dateAfter, dateBefore) => {
  if (dateBefore.length === 14 && dateAfter.length === 14) {
    return isBefore(new Date(formatFormDate(dateBefore)), new Date(formatFormDate(dateAfter)));
  }
  return true;
};

/**
 * Check if the date is after a given date
 *
 * @param {string} date - The date supposed to be after dateToCompare, in DD / MM / YYYY format.
 * @param {string} dateToCompare - The date to compare with, in DD / MM / YYYY format
 * @returns {boolean} - True when the date is really after, false otherwise.
 */
export const isDateAfter = (date, dateToCompare) => {
  if (date.length === 14 && dateToCompare.length === 14) {
    return isAfter(new Date(formatFormDate(date)), new Date(formatFormDate(dateToCompare)));
  }
  return true;
};

/**
 * Check if the date is equal to a given date
 *
 * @param {string} date - The date supposed to be equal dateToCompare, in DD / MM / YYYY format.
 * @param {string} dateToCompare - The date to compare with, in DD / MM / YYYY format
 * @returns {boolean} - True when the date is really equal, false otherwise.
 */
export const isDateEqual = (date, dateToCompare) => {
  if (date.length === 14 && dateToCompare.length === 14) {
    return isEqual(new Date(formatFormDate(date)), new Date(formatFormDate(dateToCompare)));
  }
  return true;
};

/**
 * Generates the duration that can be used to determine year, month, days between two dates.
 *
 * @param {Date} startDate The earlier date to generate the duration.
 * @param {Date} endDate The later date to generate the duration.
 * @returns {Duration|undefined} Duration if the comparison is correct.
 */
export const getDurationInterval = (startDate, endDate) => {
  if (startDate && !Number.isNaN(startDate.getTime()) && endDate && !Number.isNaN(endDate.getTime())) {
    const durationInterval = intervalToDuration({ start: startDate, end: endDate });
    if (!durationInterval.years){
      durationInterval.years = 0;
    }
    if (!durationInterval.months){
      durationInterval.months = 0;
    }
    if (!durationInterval.days){
      durationInterval.days = 0;
    }
    return durationInterval;
  }
  return undefined;
};

/**
 * Determines whether the duration is within 30 days of the next month.
 *
 * @param {Duration} duration This is a duration object from date-fns.
 * @returns {boolean} True if the date is within 30 days of the next month.
 */
export const isWithin30Days = (duration) => {
  if (duration) {
    // value from `days` from duration object can go upto `29` and when `days` is 30, `days` becomes `0` and increases `months` by 1
    // value from `days` from duration object will go up depending on month, so for July, it will be 30 days then on the 31st, it becomes 1 month 0 days,
    // while for June, it will be 29 days and on the 30th, it will move to 1 month 0 days
    return duration.years === 0 && ((duration.months === 0 && duration.days <= 29) || (duration.months === 1 && duration.days === 0));
  }

  return undefined;
};

/**
 * Check if input date within 30 days of cover start date
 * @param {string} inputDate - The date needed to be checked in the format of 'DD / MM / YYYY'
 * @param {string} coverDate - The cover start date in the format of 'Mon Jul 12 2021 10:00:00 GMT+1000 (Australian Eastern Standard Time)
 * @returns {boolean} - If the date is within the range, return true, else return false
 */
export const checkDateWithin30Days = (inputDate, coverDate) => {
  if (inputDate !== '' && inputDate !== null && inputDate !== undefined && coverDate !== '' && coverDate !== null && coverDate !== undefined) {
    const comparedDate = addDays(new Date(coverDate), 31); // The date that is 30 days (inclusive 30th) after cover start date
    const inputDateObject = new Date(formatFormDate(inputDate));
    if (isAfter(inputDateObject, new Date(coverDate)) && isBefore(inputDateObject, comparedDate)) return true;
  }

  return false;
};

/**
 * Check if input date within doesnt fall on the 29th, 30th, 31th day of the month
 * @param {string} inputDate - The date needed to be checked in the format of 'DD / MM / YYYY'
 * @returns {boolean} - If the date is within the range, return true, else return false
 */
export const checkDayBefore29 = (inputDate) => {
  if (inputDate && typeof inputDate === 'string') {
    const [dateDay] = inputDate.split('/');
    // Date cannot fall on the 29th, 30th, 31st day of the month
    if (dateDay < 29) {
      return true;
    }
  }
  return false;
};

/**
 * Check if the age is under 20 years and 11 months.
 *
 * @param {string} dateOfBirth - The date of DOB entered.
 * @returns {boolean} - True when the age is under 21 years and 11 months, false when the age is over 21 years and 11 months.
 */
export const under21Years11Month = (dateOfBirth) => {
  if (dateOfBirth) {
    const givenDate = new Date(formatFormDate(dateOfBirth));
    const duration = getDurationInterval(givenDate, new Date());

    if (duration) {
      return (duration.years ?? 0) <= 20 && (duration.months ?? 0) < 11;
    }
  }
  return false;
};

/**
 * Check if the age is under 31.
 *
 * @param {string} date - The date of DOB entered in DD / MM / YYYY format.
 * @param {Date} compareDate - Date to compare with DOB.
 * @returns {boolean} - True when the age is under 31, false when the age is over 31 or at 31.
 */
export const under31YearsOld = (date, compareDate = undefined) => {
  if (date.length === 14) {
    let formattedCompareDate = compareDate;
    if (compareDate !== undefined) {
      formattedCompareDate = format(compareDate, 'dd / MM / yyyy');
    }

    return calculateAge(date, formattedCompareDate) <= 30;
  }
  return true;
};

/**
 * Check if the age for the given date of birth, when compared to the comparison date, is within 30 days of turning 31.
 *
 * @param {string} dateOfBirth The date of birth that will need to be compared with.
 * @param {Date} compareDate The date object that will be compared with.
 * @returns {boolean} True if the compare date is within 30 days of the date of birth being 31.
 */
export const turning31In30Days = (dateOfBirth, compareDate) => {
  if (dateOfBirth && compareDate && !Number.isNaN(compareDate.getTime())) {
    const givenDate = new Date(formatFormDate(dateOfBirth));
    const duration = getDurationInterval(givenDate, compareDate);

    if (duration) {
      const birthdayInCompareDateYear = new Date(new Date(givenDate).setFullYear(compareDate.getFullYear()));
      return duration.years === 30 && checkDateWithin30Days(format(birthdayInCompareDateYear, 'dd / MM / yyyy'), compareDate);
    }
  }
  return false;
};

/**
 * Check if the age for the given date of birth, when compared to the comparison date, is within 21 and 30 years (inclusive), or within 30 days of turning 21.
 *
 * @param {string} dateOfBirth The date of birth to be calculated against.
 * @param {Date} compareDate @optional The date object that will be used to determine if the age is within 30 days of 21. if not given, use current date
 * @returns {boolean} True if the age is between 21 and 30 (inclusive), or turning 21 within 30 days of the compare date.
 */
export const withinStudentAge = (dateOfBirth, compareDate = new Date(getCurrentDate())) => {
  if (dateOfBirth && compareDate && !Number.isNaN(compareDate.getTime())) {
    const givenDate = new Date(formatFormDate(dateOfBirth));
    const duration = getDurationInterval(givenDate, compareDate);

    if (duration) {
      const age = calculateAge(dateOfBirth, format(compareDate, 'dd / MM / yyyy'));
      const birthdayInCompareDateYear = new Date(new Date(givenDate).setFullYear(compareDate.getFullYear()));
      return (age >= 21 && age <= 30) || (age === 20 && checkDateWithin30Days(format(birthdayInCompareDateYear, 'dd / MM / yyyy'), compareDate));
    }
  }
  return false;
};

/**
 * Check if the age is over 65 with the given date of birth
 *
 * @param {string} dateOfBirth The date of birth to be calculated against.
 * @returns {boolean} True if the age is over or equal to 65
 */
export const isAgeOver65 = (dateOfBirth) => {
  if (dateOfBirth) {
    const age = calculateAge(dateOfBirth);
    if (age >= 65) return true;
  }
  return false;
};

// FIXME: The `withinAge20Y11MTo24` util function is currently unused so consider deleting in the future.
/**
 * This function is similar to "withinStudentAge",
 * but instead of calculating the age range of 4 weeks (28 days) from turning 21 to 24,
 * it will calculate the age range of 20 years 11 months to 24 where the last month to 21 can range from 28-31 days.
 * e.g. if DOB is '2000-11-21' and today is '2021-10-21' it returns True (20y 11m 0d),
 * but for DOB '2000-11-23' (20y 10m 28d) return false
 *
 * @param {string} dateOfBirth The date of birth to be calculated against.
 * @param {Date} compareDate The date object that will be used to determine the age up to "compareDate".
 * @returns {boolean} True if the age is between 20 years and 11 months - 24 .
 */
export const withinAge20Y11MTo24 = (dateOfBirth, compareDate) => {
  if (dateOfBirth && compareDate && !Number.isNaN(compareDate.getTime())) {
    const givenDate = new Date(formatFormDate(dateOfBirth));
    const duration = getDurationInterval(givenDate, compareDate);
    if (duration) {
      const age = calculateAge(dateOfBirth, format(compareDate, 'dd / MM / yyyy'));
      return !under21Years11Month(dateOfBirth) && age <= 24;
    }
  }
  return false;
};

/**
 * Returns the date of student declaration year based on if it's before or after 1st of March
 *
 * @param {string} date The quote date
 * @returns {string} If date is after 1st March, returns the given date year, otherwise, returns the year before.
 *
 */
export const getStudentDeclarationYear = (date) => {
  const MARCH = 3;

  // Check if the date is already in a date object
  let givenDate;
  if (date && typeof date === 'string') {
    givenDate = new Date(formatFormDate(date));
  } else if (date && !Number.isNaN(date.getTime())) {
    givenDate = date;
  }

  if (givenDate) {
    if (givenDate.getMonth() + 1 >= MARCH) {
      return givenDate.getFullYear().toString();
    }
    return (givenDate.getFullYear() - 1).toString();
  }
  return '';
};

/**
 * Returns the date in string
 *
 * @param {Date} date Date object
 * @param {bool} isYear Flag to show year or not
 * @param {bool} isLong Flag to display month in shorthanded or not
 * @returns {string} in `dd/mmm/yyyy` or `dd/mmm` or `dd/mmmm/yyyy` or `dd/mmmm`
 */
export const convertDateToString = (date, isYear = false, isLong = false) => {
  return `${format(date, 'dd')} ${format(date, isLong ? 'MMMM' : 'MMM')} ${isYear ? format(date, 'y') : ''}`.trim();
};

/**
 * Returns either date string or date object for next payment based on the given date and payment frequency
 *
 * @param {Date} date previous payment date based on this "next payment" date
 * @param {bool} inString Flag to return in string or date object. default to true
 * @param {bool} isYear Flag to show year or not. default to false. if @param inString is false, this has no effect.
 * @param {bool} isLong Flag to show shorthanded month or not. default to false. if @param inString is false, this has no effect.
 * @returns {string || Date} If date is after 1st March, returns the given date year, otherwise, returns the year before. if @param inString is false, return in date object.
 *
 */
export const getNextPaymentDate = (date, frequency, inString = true, isYear = false, isLong = false) => {
  switch (frequency) {
    case PaymentFrequency.FORTNIGHTLY:
      return inString ? convertDateToString(addWeeks(date, 2), isYear, isLong) : addWeeks(date, 2);
    case PaymentFrequency.MONTHLY:
      return inString ? convertDateToString(addMonths(date, 1), isYear, isLong) : addMonths(date, 1);
    case PaymentFrequency.QUARTERLY:
      return inString ? convertDateToString(addMonths(date, 3), isYear, isLong) : addMonths(date, 3);
    case PaymentFrequency.SEMI_ANNUAL:
      return inString ? convertDateToString(addMonths(date, 6), isYear, isLong) : addMonths(date, 6);
    default:
      return inString ? convertDateToString(addYears(date, 1), isYear, isLong) : addYears(date, 1);
  }
};

export const isYearLeapYear = (year) => {
  if (year && typeof year === 'string') {
    return isLeapYear(new Date(year));
  }

  return false;
};

/**
 * Get the last day of given month and year, and generate the date in the format of "DD / MM / YYYY"
 * @param {string} yearValue
 * @param {string} monthValue
 * @returns {string} - A date in the format of "DD / MM / YYYY"
 */
export const getDateWithLastDay = (yearValue, monthValue) => {
  // Format to year to 20XX
  let formattedYear = yearValue;
  if (yearValue.length === 2) {
    formattedYear = `20${yearValue}`;
  }
  return format(new Date(parseInt(formattedYear, 10), parseInt(monthValue, 10), 0), 'dd / MM / yyyy');
};

/**
 * Get Date object with given timezone
 * @param {string} timezone - format should follow javascript Date object timezone eg: "Australia/Melbourne"
 * @return {Date}
 */
export const getDateWithTimezone = (timezone) => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
};

/**
 * Check if current time falls in range of business hour
 * @param {string} startTime - time string in "HH:MM:SS" format in melbourne time.
 * @param {string} endTime - time string in "HH:MM:SS" format in melbourne time.
 * @returns {boolean}
 */
export const isBusinessHours = (startTime, endTime) => {
  const startTimeArr = startTime.split(':');
  const endTimeArr = endTime.split(':');
  const baseTimezone = 'Australia/Melbourne';
  const localTimeFormat = 'en-GB';

  // setting local time string to be in 24hr format
  let now = getDateWithTimezone(baseTimezone).toLocaleTimeString(localTimeFormat).split(':');
  let start = getDateWithTimezone(baseTimezone);
  let end = getDateWithTimezone(baseTimezone);

  // get date obj for start time and end time
  for (let i = 0; i < 3; i++) {
    switch (i) {
      case 0:
        start.setHours(startTimeArr[i]);
        end.setHours(endTimeArr[i]);
        break;

      case 1:
        start.setMinutes(startTimeArr[i]);
        end.setMinutes(endTimeArr[i]);
        break;

      case 2:
        start.setSeconds(startTimeArr[i]);
        end.setSeconds(endTimeArr[i]);
        break;

      default:
        break;
    }
  }

  // setting local time string to be in 24hr format
  start = start.toLocaleTimeString(localTimeFormat).split(':');
  end = end.toLocaleTimeString(localTimeFormat).split(':');

  now = now[0] * 60 * 60 + now[1] * 60 + now[1];
  start = start[0] * 60 * 60 + start[1] * 60 + start[1];
  end = end[0] * 60 * 60 + end[1] * 60 + end[1];

  return now > start && now < end;
};

/**
 * this method converts HH:MM:SS to HH:MM am/pm in 12hrs format
 */
export const convertTo12HRFormat = (HHMMSS) => {
  const time = HHMMSS.split(':');
  const ampm = time[0] >= 12 ? 'pm' : 'am';
  let hr = Number(time[0]);
  hr = hr > 12 ? hr - 12 : hr;

  return `${hr}:${time[1]}${ampm}`;
};

/**
 * Returns boolean if given DOB meets requirement for being eligible child.
 * eligible child = aged 0 to aged 31 days prior to turning 21.
 *
 * @param {string} dob string of date in `dd / mm / yyyy`
 * @param {Date} dateBasedOn @optional date to compare with `dob`. if not given, will take current date
 * @return {bool} return true if given date of birth meets definition of eligible child. if `dob` isn't not complete, will return false.
 */
export const isEligibleChild = (dob, dateBasedOn = new Date()) => {
  if (dob.length === 14) {
    const givenDate = formatFormDate(dob);
    const age = calculateAge(dob, format(dateBasedOn, 'dd / MM / yyyy'));
    const birthdayInCompareDateYear = new Date(givenDate);
    const duration = getDurationInterval(birthdayInCompareDateYear, dateBasedOn);

    if (duration.years === 20) {
      // `intervalToDuration` (which is used within `getDurationInterval`) only counts days upto 30 and from 31st day, it returns 1 for `months` and 0 for `days`
      return (duration.days <= 30 && duration.months < 11) || (duration.days === 0 && duration.months === 1);
    }

    return age < 21;
  }

  return false;
};

/**
 * Returns the date timestamp in UTC and follow the format yyyy-MM-ddTHH:mm:ss
 *
 * @param {Date} date Date object
 * @returns {string} in yyyy-MM-ddTHH:mm:ssZ, e.g."2022-06-22T13:23:23Z"
 */
export const getUTCTime = (date = new Date()) => {
  return `${date.toISOString().split('.')[0]}Z`;
};

/**
 * Returns student declaration start date and end date in `yyyy-MM-dd` format.
 * Calculates start and end date based on following business rule: Student declaration starts on 1 March and ends on last day of Feb of following year(considering leap years).
 * @param {string} date @optional date on which declaration is being signed on, if not provided, will take current date
 * @returns {StudentDeclarationDates} Object with start and end dates for student declaration under `studentStartDate` and `studentEndDate` keys.
 */
export const getStudentDeclarationStartAndEndDate = (date = new Date()) => {
  const MARCH = 3;

  let givenDate;
  if (date && typeof date === 'string') {
    givenDate = new Date(formatFormDate(date));
  } else if (date && !Number.isNaN(date.getTime())) {
    givenDate = date;
  }

  let declarationYear;
  if (givenDate) {
    if (givenDate.getMonth() + 1 >= MARCH) {
      declarationYear = givenDate.getFullYear();
    } else {
      declarationYear = givenDate.getFullYear() - 1;
    }
  }
  const returnObj = {};

  if (declarationYear) {
    returnObj.studentStartDate = `${declarationYear}-03-01`;

    const studentEndYear = `${declarationYear + 1}`;
    let studentEndDay = '28';
    if (isYearLeapYear(studentEndYear)) {
      studentEndDay = '29';
    }
    returnObj.studentEndDate = `${studentEndYear}-02-${studentEndDay}`;
  }

  return returnObj;
};
/**
 * Returns true if `inputDate` is within `days` days (inclusive) from `targetDate`, otherwise false
 * @param {string} inputDate Date being checked in format of `DD / MM / YYYY`
 * @param {string} targetDate Reference date to calculate difference in days in format `DD / MM / YYYY`
 * @param {number} days Number of days to check the difference between inputDate and targetDate
 * @returns {boolean} True if the input date is within the given number of days from the target date, false otherwise
 */
export const isWithinNdays = (inputDate, targetDate, days) => {
  if (inputDate && targetDate && days) {
    const inputDateObj = new Date(formatFormDate(inputDate));
    const targetDateObj = new Date(formatFormDate(targetDate));
    const diff = differenceInDays(targetDateObj, inputDateObj);
    return diff <= days && diff >= 0;
  }
  return false;
};

/**
 * Adds minutes to the given date and returns the result in ISO8601 format.
 *
 * @param {Date} date Date object
 * @param {number} minutes Number of minutes to add
 * @returns {string} in ISO8601 format, e.g., "2024-04-29T07:28:16Z"
 */
export const addMinutesToDateTime = (date, minutes) => {
  const updatedDate = new Date(date.getTime() + minutes * 60 * 1000);
  return `${updatedDate.toISOString().split('.')[0]}Z`; // Format as ISO8601
};
